import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, PropertyValues, svg } from 'lit'
import { csColorBorder } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { tableStyle } from './table.style.js'


type TableVariant = 'primary' | 'secondary' | 'line'

@customElement('csc-table-header-cell')
export class CSCTableHeaderCell extends LitElement {
  @property({ type: String, attribute: 'content' }) content: string = ''

  @property({ type: String, attribute: 'variant' }) variant: TableVariant = 'primary'

  @property({ type: String, attribute: 'align' }) align: string = 'left'

  @property({ type: Boolean, attribute: 'truncate', reflect: true }) truncate: boolean = false

  private _cellStyling() {
    if (this.align === 'center') {
      this.style.justifyContent = 'center'
      this.style.textAlign = 'center'
    } else if (this.align === 'right') {
      this.style.justifyContent = 'end'
      this.style.textAlign = 'right'
    } else {
      this.style.justifyContent = ''
      this.style.textAlign = ''
    }
  }

  private _titleStyling() {
    this.title = this.truncate ? this.content : ''
  }

  override createRenderRoot = () => this

  override render() {
    const variant = this.variant === 'primary' ? 'heading-6' : 'body-s'
    this._cellStyling()
    this._titleStyling()

    return html`
      <cse-text as="span" variant="${variant}">
        ${this.content}
      </cse-text>
    `
  }
}

@customElement('csc-table-cell')
export class CSCTableCell extends LitElement {
  @property({ type: String }) content: string = ''
  @property({ type: String, attribute: 'align' }) align: string = 'left'

  private _cellStyling() {
    if (this.align === 'center') {
      this.style.justifyContent = 'center'
      this.style.textAlign = 'center'
    } else if (this.align === 'right') {
      this.style.justifyContent = 'end'
      this.style.textAlign = 'right'
    } else {
      this.style.justifyContent = ''
      this.style.textAlign = ''
    }
  }

  override createRenderRoot = () => this

  override render() {
    this._cellStyling()

    if (typeof this.content === 'object') {
      return this.content
    }

    return html`
        <cse-text as="span" variant="body">${this.content}</cse-text>
    `
  }
}

@customElement('csc-table')
export class CSCTable extends LitElement {
  static override styles: CSSResultGroup = [tableStyle]

  @property({ type: Array, attribute: 'columns' })
  columns: any = []

  @property({ type: Array, attribute: 'data' })
  data: any = []

  @property({ type: String, attribute: 'variant' })
  variant: TableVariant = 'primary'

  @state()
  loading: boolean = true

  get header(): CSCTableHeaderCell[] {
    return [...this.renderRoot.querySelectorAll<CSCTableHeaderCell>('csc-table-header-cell')]
  }

  get rows(): HTMLDivElement[] {
    return [...this.renderRoot.querySelectorAll<HTMLDivElement>('.row-wrapper')]
  }

  override willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('data') && this.data && this.data.length > 0) {
      this.loading = false
    }
  }

  private _gridStyling() {
    this.style.gridTemplateColumns = this.columns.map((column: any) => column.width ? column.width : 'auto').join(' ')
    this.style.border = (this.variant === 'line' || this.variant === 'secondary') ? `1px solid ${csColorBorder.light}` : 'none'
  }

  private _renderHeaderCells() {
    return html`
      <div class="header-wrapper">
        ${this.columns.map((column: any) => {
          return html`
            <csc-table-header-cell
              variant="${this.variant}"
              .align="${column.align || 'left'}"
              .content="${column.name}"
              ?truncate="${Boolean(column.truncate)}"
            ></csc-table-header-cell>
          `
        })}
      </div>
    `
  }

  private _renderLoadingSVG(charCount: number = 10, opacity: number = 0.6, delay: number = 0) {
    return svg`
      <svg class="loading" width="${charCount}ch" height="1em" style="opacity: ${opacity};">
        <rect width="100%" height="100%" rx="4">
          <animate attributeName="opacity" values="1;0.2;1" dur="1.5s" begin="${delay}s" repeatCount="indefinite" />
        </rect>
      </svg>
    `
  }

  private _renderDataCells() {
    if (this.loading && this.columns.length > 0) {
      return Array.from({ length: 5 }).map((_, index: number) => {
        return this.columns.map((column: any) => {
          const columnName = column.name instanceof String ? column.name : column.name.toString()
          const columnLength = columnName?.length || 0
          const opacity = 0.6 - (index * 0.1)
          const delay = index * 0.2
          const cellContent = this._renderLoadingSVG(columnLength, opacity, delay)

          return html`
            <csc-table-cell
              .align="${column.align || 'left'}"
              .content="${cellContent}"
            ></csc-table-cell>
          `
        })
      })
    }

    return this.data.map((row: any, index: number) => {
      return html`
        <div class="row-wrapper">
          ${this.columns.map((column: any) => {
            let cellContent = row[column.key] || ''
            if (column.cellRenderer && typeof column.cellRenderer === 'function') {
              cellContent = column.cellRenderer(row[column.key], row)
            }
    
            const lastRow = (this.data.length - 1) == index
            const classInfo = { 'last-row': lastRow, 'secondary-row': index % 2 === 1 }
    
            return html`
              <csc-table-cell
                class="${classMap(classInfo)}"
                variant="${this.variant}"
                .align="${column.align || 'left'}"
                .content="${cellContent}"
              ></csc-table-cell>
            `
          })}
        </div>
      `
    })
  }

  override render() {
    this._gridStyling()

    return html`
      ${this._renderHeaderCells()}
      ${this._renderDataCells()}
    `
  }
}
